// ORIGIN BLOCK — "where did this EOI / deal come from" on the EOI card and the
// Deal expanded row (owner brief 2026-10-26).
//
// ONE collapsed line under the card header —
//   🔗 From Lead · Facebook · Sahel · 21/10 · Youssef Salama + Mariam Yasser ▾
// (source · campaign · date · closers; the project is in the expanded hop)
// — that expands on tap into the full chain, one hop per line with its date.
// Collapsed by default, remembered per user. Every value is read from a FROZEN
// field; a hop that did not happen (no EOI stage) is simply absent, and a value
// nobody recorded renders "—", never a guess.
//
// WHO IS NAMED — owner clarification 2026-10-26: ONLY the agent(s) who CLOSED
// the record — the EOI/Deal's credited agent plus the split partner when the
// deal is shared. No agent at registration, no assignment chain, no previous
// holders: rotation history stays hidden (R4). The lead's agentHistory and the
// request's agentId are therefore never read here.
//
// WHAT IS FROZEN, AND WHERE (measured on production 2026-10-26, 70 records):
//   • the record's own birth        Eoi/Deal.createdAt (row._recordedAt) and
//                                   its credited agent + split partner
//                                   (row.eoiAgentId / dealAgentId,
//                                   row.eoiSplitAgent2Id / dealSplitAgent2Id).
//   • the absorbed EOI stage        Deal.eoiId / eoiRecordedAt (row._eoiId /
//     on a converted deal           row._eoiRecordedAt). Blank on a direct deal.
//   • the lead's registration       Lead.createdAt / source / campaign; the
//                                   registration PROJECT is the server's ONE
//                                   resolver (origin-project.js: frozen →
//                                   history → earliest Meeting → current),
//                                   projected as row._originProject with its
//                                   rule in row._originProjectRule — "current"
//                                   is flagged in the hop's tooltip.
//   • the Daily Request behind a    Lead.sourceRequestId → GET /api/daily-
//     DR-born record                requests/:id, fetched LAZILY on expand (the
//                                   card itself asks for nothing new on open).
//   • a lead moved from a DR        history[0] {event:"converted_from_dr"} names
//     ("Move to Leads as Fresh")    the request; source "Daily Request → Lead".
//
// NO IDS ARE RENDERED. ID rule 2 (owner-ruled, final): a client has no number,
// so "From Lead" / "From Daily Request" are links that OPEN the record, not
// labels that number it.
//
// Everything that decides what the block says is a pure module-scope function
// of (row, kind, dr, resolvers), so a gate can RUN it over a row shape.
import React, { useState, useEffect } from "react";
import { fmtDateTime12 } from "./timeFormat";

var ORIGIN_OPEN_KEY = function (userId) { return "crm_origin_open:" + String(userId || ""); };
export var readOriginOpen = function (userId) {
  try { return localStorage.getItem(ORIGIN_OPEN_KEY(userId)) === "1"; } catch (e) { return false; }
};
export var writeOriginOpen = function (userId, open) {
  try { localStorage.setItem(ORIGIN_OPEN_KEY(userId), open ? "1" : "0"); } catch (e) { /* private mode */ }
};

var idOf = function (ref) { if (!ref) return ""; return typeof ref === "object" ? String(ref._id || ref.id || "") : String(ref); };
var ms = function (v) { var t = v ? new Date(v).getTime() : NaN; return isNaN(t) ? 0 : t; };
var norm = function (s) { return String(s || "").trim().toLowerCase(); };

// Cairo-local, like every other instant the app paints.
export var fmtOriginDay = function (v) {
  if (!ms(v)) return "";
  return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", timeZone: "Africa/Cairo" });
};
export var fmtOriginWhen = function (v) {
  if (!ms(v)) return "—";
  return fmtDateTime12(v, undefined, { numeric: true, sep: " " });
};

// The Daily Request a moved-as-fresh lead came from — frozen in the lead's
// first history entry by POST /api/daily-requests/move-to-leads.
export var movedFromDrId = function (lead) {
  var h = ((lead && lead.history) || []).find(function (x) { return x && x.event === "converted_from_dr"; });
  var m = h && /Daily Request ([0-9a-f]{24})/i.exec(String(h.description || ""));
  return m ? m[1] : "";
};

// THE AGENTS WHO CLOSED IT — everyone the record itself credits, the same
// resolution the Deals page's getAg uses (owner brief 2026-10-27):
//   • the credited internal agent (creditedName, from txnAgentNameOf — legacy
//     free-text agent included) plus the split partner when shared;
//   • on an external deal, the internal agent working it beside the broker
//     (externalSalesAgentEnabled → ext.externalAgentName) and the broker as
//     "🤝 <name> (broker)" — both when both exist: "Ahmed Kassem + 🤝 Mina Samy
//     (broker)"; a broker-only deal is "🤝 Mina Samy (broker)".
// "" (rendered "—") only when none of those resolves. Never a previous holder.
// Names are resolved by id through the shared resolvers by the caller; nothing
// here paints an id.
export var closersOf = function (row, kind, nameOf, creditedName, ext) {
  row = row || {}; ext = ext || {};
  var names = [];
  var push = function (n) { n = String(n || "").trim(); if (n && names.indexOf(n) < 0) names.push(n); };
  push(creditedName);
  push(nameOf(row[kind === "deal" ? "dealSplitAgent2Id" : "eoiSplitAgent2Id"]));
  if (row.dealType === "external") {
    push(ext.externalAgentName);
    if (ext.brokerName) push("🤝 " + String(ext.brokerName).trim() + " (broker)");
  }
  return names.join(" + ");
};

// THE MODEL. (row, kind, dr, nameOf, creditedName) → everything the block paints.
//   row          the hydrated panel row (lead first, record second — hydrateTxnRow)
//   kind         "eoi" | "deal"
//   dr           the Daily Request document once fetched, else null
//   nameOf(ref)  the shared user resolver (userNameOf) — "" when unresolved
//   creditedName the record's credited agent (txnAgentNameOf)
//   ext          { brokerName, externalAgentName } — the external deal's broker and
//                the internal agent working it, resolved by the caller
export var originModel = function (row, kind, dr, nameOf, creditedName, ext) {
  row = row || {};
  nameOf = nameOf || function () { return ""; };
  var isMirror = !!row.sourceRequestId || row.source === "Daily Request";
  var movedDr = movedFromDrId(row);
  var isMoved = !isMirror && (!!movedDr || row.source === "Daily Request → Lead");
  var fromKind = isMirror ? "dr" : (isMoved ? "dr-lead" : "lead");
  var drId = isMirror ? idOf(row.sourceRequestId) : movedDr;
  var drLoaded = !!(dr && drId && idOf(dr._id) === drId);
  var closers = closersOf(row, kind, nameOf, creditedName, ext);

  // ── Registered: the ORIGIN record's own birth (no agent — see the header) ──
  // Record rows carry the lead's project under _originProject (rowFromTxnRecord:
  // the frozen originProject, else the lead's stored project — row.project is
  // the RECORD's). A record-less lead row IS the lead, so its own field answers.
  var leadOriginProject = row._fromRecord
    ? (String(row._originProject || "").trim() || null)
    : (String(row.originProject || row.project || "").trim() || null);
  // How the server resolved it (frozen / history / meeting / current). "current"
  // means the registration project was never recorded and the lead's current
  // development stands in — the hop says so in its tooltip (owner ruling
  // 2026-10-27). A record-less lead row has no rule; it reads its own field.
  var originRule = row._fromRecord ? String(row._originProjectRule || "") : (String(row.originProject || "").trim() ? "frozen" : "current");
  var reg;
  if (fromKind === "lead") {
    reg = { date: row.createdAt || null, source: row.source || "", campaign: row.campaign || "", project: leadOriginProject, known: true };
  } else if (drLoaded) {
    reg = { date: dr.createdAt || null, source: dr.source || "Daily Request", campaign: "",
            project: (dr.originProject && String(dr.originProject).trim()) || (dr.project && String(dr.project).trim()) || null, known: true };
  } else {
    reg = { date: null, source: "Daily Request", campaign: "", project: null, known: false };
  }

  // ── The hops, in order. A hop that did not happen is not in the list. ──
  var hops = [];
  if (fromKind !== "lead") {
    hops.push({ key: "dr", label: "Daily Request", date: reg.date, link: drId ? "dr" : "",
                unlinked: !drId, pending: !!drId && !drLoaded,
                meta: [reg.source, reg.project === null ? (drLoaded ? "project —" : "") : reg.project].filter(Boolean) });
  }
  if (fromKind === "lead" || fromKind === "dr-lead") {
    hops.push({ key: "lead", label: fromKind === "dr-lead" ? "Moved to Leads" : "Lead registered",
                date: row.createdAt || null, link: "lead",
                title: originRule === "current" && leadOriginProject ? "project: current value — the registration project was never recorded" : "",
                meta: fromKind === "dr-lead"
                  ? [row.source || "", leadOriginProject === null ? "project —" : leadOriginProject].filter(Boolean)
                  : [reg.source, reg.campaign, reg.project === null ? "project —" : reg.project].filter(Boolean) });
  }
  var hadEoi = kind === "eoi" || !!row._eoiId || !!row._eoiRecordedAt;
  if (hadEoi) {
    hops.push({ key: "eoi", label: "EOI created", date: kind === "eoi" ? (row._recordedAt || null) : (row._eoiRecordedAt || null), who: closers });
  }
  if (kind === "deal") {
    hops.push({ key: "deal", label: hadEoi ? "Converted to Deal" : "Deal closed", date: row._recordedAt || null, who: closers });
  }

  // ── Project changed: the record's project vs the project it was REGISTERED for ──
  var recProject = String(row.project || "").trim();
  var projectChanged = (reg.project && recProject && norm(reg.project) !== norm(recProject))
    ? { from: reg.project, to: recProject } : null;

  // ── The collapsed line ──────────────────────────────────────────────────
  var fromLabel = fromKind === "dr" ? "From Daily Request" : (fromKind === "dr-lead" ? "From Daily Request → Lead" : "From Lead");
  // "From Lead · <source> · <campaign> · <date> · <closer>" (owner ruling
  // 2026-10-27). The PROJECT is NOT on this line — it lives in the expanded
  // "Lead registered" hop. The first cut put the registration project here,
  // which read as a campaign ("Facebook · Silversands") on #50033. An empty
  // campaign shows nothing, never "—". On a DR-born record the source is the
  // label itself ("Daily Request") and a request has no campaign, so the line
  // is just the date and the closers.
  var sourceTok = (fromKind === "lead" || norm(reg.source) !== "daily request") ? reg.source : "";
  var summary = [sourceTok, reg.campaign || "", fmtOriginDay(reg.date), closers].filter(Boolean);

  return { fromKind: fromKind, drId: drId, drLoaded: drLoaded, leadId: idOf(row._id), reg: reg, closers: closers,
           hops: hops, projectChanged: projectChanged, fromLabel: fromLabel, summary: summary };
};

// ── THE COMPONENT ─────────────────────────────────────────────────────────
// props: row, kind, cu, token, apiFetch, nameOf, creditedName, brokerName,
//        externalAgentName, eoiContact (the EOI-time buyer name when it differs
//        from the client — a deal-only inline note, phones in eoiContactTitle),
//        onOpenLead(id), onOpenDr(drDoc|id), colors {text, textLight, border}
// VISIBILITY is the caller's: both panels mount this behind their own
// isOnlyAdmin (admin + operation, owner ruling 2026-10-27).
// Mount it keyed by the row (key={rowKey(row)}) so the lazy DR fetch and the
// open state never carry over from one record to the next.
export function OriginBlock(props) {
  var cu = props.cu || {};
  var uid = cu.id || cu._id || "";
  var row = props.row || {};
  var C = props.colors || {};
  var muted = C.textLight || "#64748B";
  var [open, setOpen] = useState(function () { return readOriginOpen(uid); });
  var [dr, setDr] = useState(null);
  var [drState, setDrState] = useState("");   // "" | "loading" | "unavailable"

  var ext = { brokerName: props.brokerName || "", externalAgentName: props.externalAgentName || "" };
  var base = originModel(row, props.kind, null, props.nameOf, props.creditedName, ext);
  var drId = base.drId;

  // LAZY: the request is asked for on expand, never on card open.
  useEffect(function () {
    if (!open || !drId || !props.apiFetch) return;
    if (dr && idOf(dr._id) === drId) return;
    var cancelled = false;
    setDrState("loading");
    props.apiFetch("/api/daily-requests/" + drId, "GET", null, props.token)
      .then(function (d) {
        if (cancelled) return;
        if (d && d._id) { setDr(d); setDrState(""); } else { setDrState("unavailable"); }
      })
      .catch(function () { if (!cancelled) setDrState("unavailable"); });
    return function () { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, drId]);

  var model = originModel(row, props.kind, (dr && idOf(dr._id) === drId) ? dr : null, props.nameOf, props.creditedName, ext);
  var toggle = function (e) { if (e && e.stopPropagation) e.stopPropagation(); var n = !open; setOpen(n); writeOriginOpen(uid, n); };
  var linkStyle = { background: "none", border: "none", padding: 0, margin: 0, font: "inherit", color: "inherit",
                    textDecoration: "underline", textUnderlineOffset: 2, cursor: "pointer" };
  var openLead = function (e) { e.stopPropagation(); if (props.onOpenLead && model.leadId) props.onOpenLead(model.leadId); };
  var openDr = function (e) { e.stopPropagation(); if (props.onOpenDr && drId) props.onOpenDr((dr && idOf(dr._id) === drId) ? dr : drId); };

  var fromNode = model.fromKind === "lead"
    ? <button type="button" onClick={openLead} style={linkStyle} data-origin-link="lead">{model.fromLabel}</button>
    : (drId
        ? <button type="button" onClick={openDr} style={linkStyle} data-origin-link="dr">{model.fromLabel}</button>
        : <span>{model.fromLabel}</span>);

  return <div data-origin-block={props.kind} style={{ padding: "6px 14px 0", fontSize: 12, color: muted, lineHeight: 1.45 }}>
    <div onClick={toggle} role="button" aria-expanded={open} data-origin-toggle="1"
         style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", minWidth: 0 }}>
      <span style={{ flexShrink: 0 }}>🔗</span>
      <span style={{ minWidth: 0, flex: "1 1 auto", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {fromNode}
        {model.summary.map(function (s, i) { return <span key={i}> · {s}</span>; })}
        {!model.summary.length && model.drId && !model.drLoaded && <span> · …</span>}
        {props.eoiContact && <span data-origin-eoi-contact="1" title={props.eoiContactTitle || undefined}> · EOI contact: {props.eoiContact}</span>}
      </span>
      <span style={{ flexShrink: 0, opacity: 0.7 }}>{open ? "▴" : "▾"}</span>
    </div>
    {open && <div data-origin-chain="1" style={{ margin: "4px 0 2px 18px", borderLeft: "1px solid " + (C.border || "#E2E8F0"), paddingLeft: 10 }}>
      {model.hops.map(function (h) {
        var label = h.link === "lead"
          ? <button type="button" onClick={openLead} style={linkStyle} data-origin-link="lead">{h.label}</button>
          : (h.link === "dr"
              ? <button type="button" onClick={openDr} style={linkStyle} data-origin-link="dr">{h.label}</button>
              : <span>{h.label}</span>);
        var waiting = h.pending && drState !== "";
        return <div key={h.key} data-origin-hop={h.key} title={h.title || undefined} style={{ padding: "2px 0", whiteSpace: "normal", wordBreak: "break-word" }}>
          <span style={{ color: C.text || "#1E293B", fontWeight: 600 }}>{label}</span>
          {h.pending && drState === "loading" && <span> · loading…</span>}
          {h.pending && drState === "unavailable" && <span> · details not available to you</span>}
          {h.unlinked && <span> · request not linked</span>}
          {!waiting && <span> · {fmtOriginWhen(h.date)}</span>}
          {(h.key === "eoi" || h.key === "deal") && <span> · {h.who || "—"}</span>}
          {h.meta && h.meta.length > 0 && <span> · {h.meta.join(" · ")}</span>}
        </div>;
      })}
      {model.projectChanged && <div data-origin-project-changed="1" style={{ marginTop: 4, display: "inline-block", background: "#FEF3C7", color: "#B45309", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>
        project changed: {model.projectChanged.from} → {model.projectChanged.to}
      </div>}
    </div>}
  </div>;
}
